import {
    Card,
    Input,
    Button,
    Tabs,
    TabsHeader,
    Tab,
    Menu,
    MenuList,
    MenuHandler,
    MenuItem,
    IconButton,
} from "@material-tailwind/react";

import {
    MagnifyingGlassIcon,
    ArrowPathIcon,
    FunnelIcon,
    Bars3BottomLeftIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    XCircleIcon,
} from "@heroicons/react/24/solid";
import AssignAppointmentModal from "./assign-appointment-modal";

import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";

import { useDispatch, useSelector } from "react-redux";
import { cancelAppointment, completeAppointment, getAppointments, getAvailableSlots, markCustomerContacted, rejectAppointment, rescheduleAppointment } from "../../store/slices/appointmentSlice";
import dayjs from "dayjs";
import { Tooltip } from "flowbite-react";
import moment from "moment";
import Swal from "sweetalert2";
import { approveAppointment } from "@/store/slices/appointmentSlice";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import AssignTeamModal from "./assign-team-modal";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Popover } from "react-tiny-popover";
import CustomerNotificationLogModal from "./customer-notification-log-modal";
import { useTranslation } from "react-i18next";
import useHasPermission from "@/components/useHasPermission";
import { useNavigate } from 'react-router-dom';

const priorityColorMap = {
    high: "bg-red-500",
    medium: "bg-yellow-500",
    low: "bg-green-500",
};


export default function ManageAppointments() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(null);
    const [openAssignModal, setOpenAssignModal] = useState(false);
    const [openAssignTeamModal, setOpenAssignTeamModal] = useState(false);
    const [isViewLog, setIsViewLog] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortBy, setSortBy] = useState("appointment_date");

    const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState(null);
    const [rescheduleDate, setRescheduleDate] = useState("");
    const [rescheduleTime, setRescheduleTime] = useState("");
    const [rescheduleReason, setRescheduleReason] = useState("");
    const todayDateString = new Date().toISOString().slice(0, 10);

    const hasUpdatePermission = useHasPermission('appointment.update');

    const { t, i18n } = useTranslation('appointment');
    const { t: ct } = useTranslation("common");

    const getLocalized = (val) => {
        if (val && typeof val === "object") {
            const lang = i18n.language?.split("-")[0];
            return val[lang] || val.en || val.ar || Object.values(val)[0] || "";
        }
        return val;
    };

    const { user } = useSelector((state) => state.user);

    const {
        appointments,
        list_loading,
        loading,
        tab_counts,
        availableSlots
    } = useSelector(
        (state) => state.appointment
    );

    const [page, setPage] = useState(1);

    const [search, setSearch] = useState("");

    const observer = useRef();

    useEffect(() => {
        if (user?.roles[0] === 'Sales Representative') {
            setActiveTab('assigned');
        } else {
            setActiveTab('pending');
        }
    }, [user]);

    const fetchAppointments = useCallback(
        (pageNumber = 1) => {
            if (activeTab == null) return;
            dispatch(
                getAppointments({
                    page: pageNumber,
                    search,
                    tab: activeTab,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                })
            );
        },
        [
            dispatch,
            activeTab,
            search,
            sortOrder,
            sortBy
        ]
    );


    // useEffect(() => {
    //     const timer = setTimeout(() => {
    //         setPage(1);

    //         fetchAppointments(1);
    //     }, 500);

    //     return () => clearTimeout(timer);
    // }, [search]);

    const handelSearch = (e) => {
        if (e.key === "Enter") {
            setPage(1);
            fetchAppointments(1);
        } else if (e.key === "Backspace" && e.currentTarget.value.length === 0) {
            setPage(1);
            fetchAppointments(1);
        }
    };

    const handleSearchClick = () => {
        setPage(1);
        fetchAppointments(1);
    };

    const handelRemoveSearch = () => {
        setSearch("");
        setPage(1);
        dispatch(
            getAppointments({
                page: 1,
                search: "",
                tab: activeTab,
                sort_by: sortBy,
                sort_order: sortOrder,
            })
        );
    };

    useEffect(() => {
        setPage(1);
        fetchAppointments(1);
    }, [
        activeTab,
        sortOrder,
        sortBy
    ]);


    const lastAppointmentRef = useCallback(
        (node) => {

            if (list_loading) return;

            if (observer.current)
                observer.current.disconnect();

            observer.current =
                new IntersectionObserver(
                    (entries) => {

                        if (
                            entries[0].isIntersecting &&
                            appointments?.appointments?.next_page_url
                        ) {

                            const nextPage =
                                page + 1;

                            setPage(nextPage);

                            fetchAppointments(
                                nextPage
                            );
                        }
                    }
                );

            if (node)
                observer.current.observe(node);
        },
        [
            list_loading,
            appointments,
            page,
            fetchAppointments,
        ]
    );

    const formatDateTime = (date) => {
        const today = dayjs();
        const created = dayjs(date);

        if (created.isSame(today, "day")) {
            return `Today | ${created.format("h:mm A")}`;
        }

        if (created.isSame(today.subtract(1, "day"), "day")) {
            return `Yesterday | ${created.format("h:mm A")}`;
        }

        return `${created.format("DD MMM YYYY")} | ${created.format("h:mm A")}`;
    };

    const counts = tab_counts || {};

    const tabs =
        user?.roles?.includes("Sales Representative")
            ? [
                { id: "assigned", label: `${t("assignedAppointments")} (${counts.assigned || 0})` },
                { id: "confirmed", label: `${t("confirmedAppointments")} (${counts.confirmed || 0})` },
                { id: "completed", label: `${t("completedAppointments")} (${counts.completed || 0})` },
                { id: "cancelled", label: `${t("cancelledAppointments")} (${counts.cancelled || 0})` },
            ]
            : [
                { id: "pending", label: `${t("pendingAppointments")} (${counts.pending || 0})` },
                { id: "upcoming", label: `${t("upcomingAppointments")} (${counts.upcoming || 0})` },
                { id: "assigned", label: `${t("assignedAppointments")} (${counts.assigned || 0})` },
                { id: "confirmed", label: `${t("confirmedAppointments")} (${counts.confirmed || 0})` },
                { id: "completed", label: `${t("completedAppointments")} (${counts.completed || 0})` },
                { id: "cancelled", label: `${t("cancelledAppointments")} (${counts.cancelled || 0})` },
                { id: "rejected", label: `${t("rejectedAppointments")} (${counts.rejected || 0})` },
            ];


    const handleApprove = async (appointment) => {
        const result = await Swal.fire({
            title: `${t("approveAppointmentTitle")}`,
            text: `${t("approveAppointmentText")}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: `${t("yesApprove")}`,
            cancelButtonText: `${ct("cancel")}`,
            confirmButtonColor: "#16a34a",
        });

        if (!result.isConfirmed) return;

        try {
            const response = await dispatch(
                approveAppointment({
                    id: appointment.id,
                    status: "approved",
                })
            ).unwrap();

            if (response?.status) {
                fetchAppointments();
                Toaster.success(response?.message || `${t("appointmentApprovedSuccess")}`);
            } else {
                Toaster.error(response?.message || `${t("appointmentApproveFailed")}`);
            }

        } catch (error) {
            Toaster.error(error?.message || `${t("appointmentApproveFailed")}`);
        }
    };

    const handleReject = async (appointment) => {
        const { value: reason } = await Swal.fire({
            title: `${t("rejectAppointmentTitle")}`,
            input: "textarea",
            inputLabel: `${t("reasonForRejection")}`,
            inputPlaceholder: `${t("enterRejectionReason")}...`,
            inputAttributes: {
                "aria-label": `${t("enterRejectionReason")}`,
            },
            showCancelButton: true,
            confirmButtonText: `${ct("reject")}`,
            confirmButtonColor: "#dc2626",
            cancelButtonText: t("cancel"),
            inputValidator: (value) => {
                if (!value) {
                    return `${t("pleaseEnterRejectionReason")}`;
                }
                if (value.trim().length < 10) {
                    return `${t("reasonMinimumCharacters")}`;
                }

                if (value.trim().length > 500) {
                    return `${t("reasonMaximumCharacters")}`;
                }
            },
        });

        if (!reason) return;

        try {
            const response = await dispatch(
                rejectAppointment({
                    id: appointment.id,
                    reason,
                })
            ).unwrap();

            if (response?.status) {
                fetchAppointments();
                Toaster.success(response?.message || `${t("appointmentRejectedSuccess")}`);
            } else {
                Toaster.error(response?.message || `${t("appointmentRejectFailed")}`);
            }

        } catch (error) {
            Toaster.error(error?.message || `${t("appointmentRejectFailed")}`);
        }
    };

    const handleCancel = async (appointment) => {
        const { value: cancel_reason } = await Swal.fire({
            title: `${t("cancelAppointmentTitle")}`,
            input: "textarea",
            inputLabel: `${t("reasonForCancellation")}`,
            inputPlaceholder: `${t("enterCancellationReason")}...`,
            inputAttributes: {
                "aria-label": `${t("enterCancellationReason")}`,
            },
            showCancelButton: true,
            confirmButtonText: `${t("cancelAppointmentTitle")}`,
            confirmButtonColor: "#f59e0b",
            cancelButtonText: `${ct("back")}`,
            inputValidator: (value) => {
                if (!value) {
                    return `${t("pleaseEnterCancellationReason")}`;
                }
                if (value.trim().length < 10) {
                    return `${t("reasonMinimumCharacters")}`;
                }

                if (value.trim().length > 500) {
                    return `${t("reasonMaximumCharacters")}`;
                }
            },
        });

        if (!cancel_reason) return;

        try {
            const response = await dispatch(
                cancelAppointment({
                    id: appointment.id,
                    reason: cancel_reason,
                })
            ).unwrap();

            if (response?.status) {
                fetchAppointments();
                Toaster.success(
                    response?.message || `${t("appointmentCancelledSuccess")}`
                );
            } else {
                Toaster.error(
                    response?.message || `${t("appointmentCancelFailed")}`
                );
            }

        } catch (error) {
            Toaster.error(
                error?.message || `${t("appointmentCancelFailed")}`
            );
        }
    };


    const handleConfirmAppointment = async (appointmentId) => {
        const result = await Swal.fire({
            title: `${t("confirmAppointmentTitle")}`,
            text: `${t("confirmAppointmentText")}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: `${t("yesConfirm")}`,
            cancelButtonText: `${ct("cancel")}`,
            confirmButtonColor: "#16a34a",
        });

        if (!result.isConfirmed) return;

        try {
            const res = await dispatch(
                markCustomerContacted(appointmentId)
            ).unwrap();

            if (res?.status) {
                Toaster.success(`${t("appointmentConfirmedSuccess")}`);
                fetchAppointments();
            } else {
                Toaster.error(res?.message || `${t("appointmentConfirmFailed")}`);
            }
        } catch (error) {
            Toaster.error(error?.message || `${t("appointmentConfirmFailed")}`);
        }
    };

    const handleCompleteAppointment = async (appointmentId) => {
        const result = await Swal.fire({
            title: `${t("completeAppointmentTitle")}`,
            text: `${t("completeAppointmentText")}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: `${t("yesCompleteAppointment")}`,
            cancelButtonText: `${ct("cancel")}`,
            confirmButtonColor: "#16a34a",
        });

        if (!result.isConfirmed) return;

        try {
            const res = await dispatch(
                completeAppointment({
                    id: appointmentId,
                })
            ).unwrap();

            if (res?.status) {
                Toaster.success(res?.message || `${t("appointmentCompletedSuccess")}`);
                fetchAppointments();
            } else {
                Toaster.error(res?.message || `${t("appointmentCompletedFailed")}`);
            }
        } catch (error) {
            Toaster.error(error?.message || `${t("appointmentCompletedFailed")}`);
        }
    };

    const handleRescheduleSubmit = async (e, appointmentId) => {
        e.preventDefault();

        if (!rescheduleDate || !rescheduleTime || !rescheduleReason) return;

        const cleanReason = rescheduleReason.trim();

        if (cleanReason.length < 10) {
            Toaster.error(`${t("reasonMinimumCharactersLong")}`);
            return;
        }

        if (cleanReason.length > 500) {
            Toaster.error(`${t("reasonMaximumCharacters")}`);
            return;
        }

        const payload = {
            id: appointmentId,
            appointment_date: rescheduleDate,
            appointment_time: rescheduleTime,
            reason: cleanReason,
            status: "assigned",
        };

        // Close popup
        setRescheduleAppointmentId(null);

        try {
            const res = await dispatch(
                rescheduleAppointment(payload)
            ).unwrap();

            if (res?.status) {
                Toaster.success(
                    res?.message || `${t("appointmentRescheduledSuccess")}`
                );

                setRescheduleReason("");
                fetchAppointments();
            } else {
                Toaster.error(
                    res?.message || `${t("appointmentRescheduleFailed")}`
                );
            }
        } catch (error) {
            Toaster.error(
                error?.message || `${t("appointmentRescheduleFailed")}`
            );
        }
    };

    const [isPopoverOpen, setIsPopoverOpen] = useState([]);
    const openActionPopover = (index) => {
        setIsPopoverOpen((prev) => {
            if (prev.includes(index)) {
                return [];
            } else {
                return [index];
            }
        });
    };
    const popoverRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsPopoverOpen([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    useEffect(() => {
        if (!rescheduleAppointmentId || !rescheduleDate) return;
        dispatch(getAvailableSlots({ appointment_date: rescheduleDate }));
    }, [rescheduleDate, rescheduleAppointmentId]);

    return (
        <div className="p-3 md:p-6 min-h-screen">
            <ScrollToTop />
            {loading && <ShowLoader />}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold">{t("appointmentsTitle")} ({counts.all || 0})</h2>
                    <button
                        className="ml-4"
                        onClick={() => {
                            setPage(1);

                            dispatch(
                                getAppointments({
                                    page: 1,
                                    search,
                                    tab: activeTab,
                                    sort_by: "appointment_date",
                                    sort_order: sortOrder,
                                })
                            );
                        }}
                        disabled={list_loading}
                    >
                        <img
                            src="/img/icons/reload.png"
                            alt={t("reloadAlt")}
                            className={`h-4 w-4 ${list_loading ? "animate-spin" : ""
                                }`}
                        />
                    </button>
                </div>

                <div className="w-full lg:max-w-md flex items-center gap-2">
                    <div className="w-full relative max-w-md">
                        <Input
                            label={t("searchLabel")}
                            icon={!search && <MagnifyingGlassIcon className="h-5 w-5" />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyUp={handelSearch}
                            className="pr-10"
                        />
                        {search && (
                            <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                                <XCircleIcon className="h-5 w-5 text-red-500" />
                            </IconButton>
                        )}
                    </div>
                    {search && (
                        <Button
                            variant="outlined"
                            className="flex items-center border-gray-300 capitalize font-bold bg-black text-white whitespace-nowrap"
                            onClick={handleSearchClick}
                        >
                            {t("searchButton")}
                        </Button>
                    )}
                </div>

            </div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4 mt-8">


                <div className="flex items-center gap-3">
                    <Menu>
                        <MenuHandler>
                            <Button
                                variant="text"
                                className="flex items-center gap-2 bg-gray-100 text-black"
                            >
                                <Bars3BottomLeftIcon className="h-4 w-4" />
                                {t("sortButton")}
                            </Button>
                        </MenuHandler>

                        <MenuList>
                            <MenuItem
                                onClick={() => {
                                    setSortBy("appointment_date");
                                    setSortOrder("desc");
                                }}
                            >
                                {t("sortNewestAppointment")}
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    setSortBy("appointment_date");
                                    setSortOrder("asc");
                                }}
                            >
                                {t("sortOldestAppointment")}
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    setSortBy("created_at");
                                    setSortOrder("desc");
                                }}
                            >
                                {t("sortRecentlyRequested")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                    <Button
                        className="bg-[#F4B11F] text-black shadow-none hover:shadow-none capitalize"
                        onClick={() => navigate('/dashboard/create-appointment')}
                    >
                        {ct("createAppointment")}
                    </Button>


                </div>
            </div>


            {/* Custom Nav Filtering Tabs Subnavigation */}
            <div className="border-b border-gray-200/80 mb-4 md:mb-6 flex gap-4 md:gap-8 mt-6 md:mt-8 overflow-x-auto pb-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id);
                            setPage(1);
                        }}
                        className={`pb-3 text-xs !outline-0 md:text-[13px] font-bold tracking-wide relative transition-colors whitespace-nowrap ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        {tab.label}
                        {activeTab === tab.id && (
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-900 rounded-full" />
                        )}
                    </button>
                ))}
            </div>

            {/* List */}
            <div className="mt-6 md:mt-8 space-y-3 md:space-y-6 overflow-x-auto">
                {appointments?.map((appointment, index) => {
                    const appointmentDateTime = moment(
                        `${appointment.appointment_date} ${appointment.appointment_time}`,
                        "YYYY-MM-DD HH:mm:ss"
                    );
                    return (
                        <Card
                            key={appointment.id}
                            shadow={false}
                            className={`border-b border-gray-200 rounded-none p-3 md:p-4 lg:p-6 bg-transparent ${activeTab === "confirmed" ||
                                (activeTab === "assigned" && user.roles[0] === "Sales Representative")
                                ? "sm:min-w-[1400px]"
                                : "sm:min-w-[1000px]"
                                }`}
                        >
                            <div className="flex flex-col md:flex-row lg:flex-row gap-4 md:gap-6">
                                {/* Company */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {appointment?.priority && activeTab !== "upcoming" && activeTab !== "pending" && (
                                            <span
                                                className={`w-2 h-2 rounded-full ${priorityColorMap[
                                                    appointment?.priority
                                                ]
                                                    }`}
                                            ></span>
                                        )}
                                        <h4 className="font-semibold text-sm text-[#1A1918]">
                                            {appointment.customer?.company_name || `${appointment.customer?.first_name} ${appointment.customer?.last_name ? appointment.customer?.last_name : ''}` || `${t("na")}`}
                                        </h4>
                                        <Tooltip
                                            placement="right"
                                            className="!bg-white !text-black border border-gray-200 shadow-lg"
                                            content={
                                                <div className="max-w-xs text-xs text-black">
                                                    <div className="mb-2 font-semibold text-black">
                                                        {t("appointmentRef")}: {appointment.appointment_no}
                                                    </div>
                                                    <div className="mb-2 font-semibold text-black">
                                                        {t("customerInformation")}
                                                    </div>

                                                    <p>
                                                        <span className="font-medium">{t("nameLabel")}:</span>{" "}
                                                        {`${appointment.customer?.first_name || ""} ${appointment.customer?.last_name || ""
                                                            }`}
                                                    </p>

                                                    <p>
                                                        <span className="font-medium">{t("companyLabel")}:</span>{" "}
                                                        {appointment.customer?.company_name || `${t("na")}`}
                                                    </p>

                                                    <p>
                                                        <span className="font-medium">{t("emailLabel")}:</span>{" "}
                                                        {appointment.customer?.email || `${t("na")}`}
                                                    </p>

                                                    <p>
                                                        <span className="font-medium">{t("phoneLabel")}:</span>{" "}
                                                        {appointment.customer?.country_code}{" "}
                                                        {appointment.customer?.mobile_number || `${t("na")}`}
                                                    </p>

                                                    <p>
                                                        <span className="font-medium">{t("alternatePhoneLabel")}:</span>{" "}
                                                        {appointment.customer?.alternate_number
                                                            ? appointment.customer?.alternate_country_code
                                                            : ""}
                                                        {" "}
                                                        {appointment.customer?.alternate_number || `${t("na")}`}
                                                    </p>

                                                    <div className="mt-3 pt-2 border-t border-gray-200">
                                                        <button
                                                            type="button"
                                                            className="text-blue-600 hover:text-blue-700 font-medium underline"
                                                            onClick={() => setIsViewLog(appointment)}
                                                        >
                                                            {t("viewNotificationHistory")}
                                                        </button>

                                                        <p className="mt-1 text-[11px] text-gray-600">
                                                            {t("notificationHistoryDescription")}
                                                        </p>
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <InformationCircleIcon className="h-4 w-4 text-blue-gray-500 cursor-pointer hover:text-black" />
                                        </Tooltip>
                                    </div>

                                    <p className="text-xs font-medium text-gray-600 mb-1 mt-1">
                                        {t("appointmentRef")}:{" "}
                                        <span className="font-semibold text-[#1A1918]">
                                            {appointment.appointment_no}
                                        </span>
                                    </p>

                                    {appointment?.created_by_badge && (
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                            {t("createdByLabel")}:{" "}
                                            <span className="font-semibold text-[#1A1918]">
                                                {appointment.created_by_badge}
                                            </span>
                                        </p>
                                    )}


                                    <p className="text-xs md:text-sm text-gray-600 mt-2">
                                        {appointment.customer?.country_code} {appointment.customer?.mobile_number}
                                    </p>
                                </div>

                                {/* Issue */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-semibold text-[#1A1918]">
                                        {t("issueLabel")}: {getLocalized(appointment?.subject?.subject) || `${t("na")}`}
                                    </p>

                                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                                        {t("issueDetailsLabel")}:
                                    </p>

                                    <p className="text-xs md:text-sm text-gray-600 mt-3 leading-relaxed">
                                        {appointment?.notes || `${t("na")}`}
                                    </p>
                                </div>

                                {/* Date */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs md:text-sm font-semibold text-[#1A1918]">
                                        {t("dateLabel")}: {moment(appointment.appointment_date).format("Do MMMM, YYYY")}
                                    </p>

                                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                                        {t("timeLabel")}: {moment(appointment.appointment_time, "HH:mm:ss").format("hh:mm A")}
                                    </p>
                                    {(appointmentDateTime.isBefore(moment()) && activeTab === "upcoming") && (
                                        <p className="mt-2 text-xs md:text-sm font-medium text-red-600">
                                            ⚠️ {t("appointmentExpired")}
                                        </p>
                                    )}

                                </div>

                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1A1918]">
                                        {t("meetingLocationLabel")} : {
                                            appointment?.meeting_location
                                                ? appointment.meeting_location.charAt(0).toUpperCase() +
                                                appointment.meeting_location.slice(1)
                                                : `${t("na")}`
                                        }
                                    </p>

                                    {appointment?.meeting_location === "office" && (
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("officeLabel")} : {getLocalized(appointment?.office?.office_name) || `${t("na")}`}
                                        </p>
                                    )}

                                    {appointment?.meeting_location === "outside" && (
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("outsideLabel")} : {appointment?.outside_location || `${t("na")}`}
                                        </p>
                                    )}


                                    <p className="text-xs md:text-sm text-gray-600 mt-3 leading-relaxed">
                                        {appointment?.assignment_note || ""}
                                    </p>
                                </div>

                                {/* Manager and Status - Always rendered but may be hidden */}
                                <div className={`flex-1 min-w-0 ${(activeTab === "assigned" || activeTab === "completed" || activeTab === "cancelled" || activeTab === "confirmed") ? '' : 'hidden'}`}>
                                    <p className="font-semibold text-xs md:text-sm text-[#1A1918]">
                                        {t("salesManagerLabel")}: {appointment.manager?.first_name} {appointment.manager?.last_name || ''}
                                    </p>
                                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                                        {appointment.manager?.country_code} {appointment.manager?.mobile_number || `${t("na")}`}
                                    </p>
                                    <p className="font-semibold text-xs md:text-sm text-[#1A1918]">
                                        {t("salesRepLabel")}: {appointment.sales_rep?.first_name} {appointment.sales_rep?.last_name || ''}
                                    </p>
                                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                                        {appointment.sales_rep?.country_code} {appointment.sales_rep?.mobile_number || `${t("na")}`}
                                    </p>
                                </div>

                                {/* Status Details - Always rendered but may be hidden */}
                                <div className={`flex-1 min-w-0 ${(activeTab === "completed" || activeTab === "cancelled" || activeTab === "confirmed") ? '' : 'hidden'}`}>
                                    <p className="font-semibold text-xs md:text-sm text-[#1A1918]">
                                        {t("appointmentStatusLabel")} : {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                                    </p>
                                    {activeTab === "cancelled" && <>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("cancelledByLabel")} : {appointment.cancelled_by?.first_name} {appointment.cancelled_by?.last_name || ''}
                                        </p>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("cancelledAtLabel")} : {appointment.cancelled_at ? moment(appointment.cancelled_at).format("Do MMMM, YYYY | hh:mm A") : `${t("na")}`}
                                        </p>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("cancellationReasonLabel")}:
                                        </p>
                                        <p className="text-xs md:text-sm text-gray-600 mt-3 leading-relaxed">
                                            {appointment.cancel_reason || `${t("na")}`}
                                        </p>
                                    </>
                                    }
                                    {activeTab === "confirmed" && <>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("confirmedByLabel")} : {appointment.contacted_by?.first_name} {appointment.contacted_by?.last_name || ''}
                                        </p>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("confirmedAtLabel")} : {appointment.contacted_at ? moment(appointment.contacted_at).format("Do MMMM, YYYY | hh:mm A") : `${t("na")}`}
                                        </p>
                                    </>
                                    }
                                    {Number(appointment.reschedule_count) > 0 && (
                                        <p className="text-xs md:text-sm text-[#D88900] mt-2">
                                            {t("rescheduledLabel")} : {appointment.reschedule_count || `${t("na")}`} {appointment.reschedule_count === 1 ? `${t("timeSingular")}` : `${t("timePlural")}`}
                                        </p>
                                    )}
                                </div>
                                <div className={`flex-1 min-w-0 ${(activeTab === "rejected") ? '' : 'hidden'}`}>
                                    <p className="font-semibold text-xs md:text-sm text-[#1A1918]">
                                        {t("appointmentStatusLabel")} : {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                                    </p>
                                    {activeTab === "rejected" && <>
                                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                                            {t("rejectionReasonLabel")}:
                                        </p>
                                        <p className="text-xs md:text-sm text-gray-600 mt-3 leading-relaxed">
                                            {appointment.rejection_reason || `${t("na")}`}
                                        </p>
                                    </>
                                    }
                                </div>


                                <div
                                    className={`flex-1 min-w-0 flex flex-col md:flex-row lg:justify-end gap-2 md:gap-4 mt-4 md:mt-0 ${(
                                        (activeTab === "pending" && !list_loading) ||
                                        (activeTab === "upcoming" && !list_loading) ||
                                        (activeTab === "assigned" &&
                                            !list_loading &&
                                            user.roles[0] === "Sales Representative") ||
                                        (activeTab === "confirmed" && !list_loading)
                                    )
                                        ? ""
                                        : "hidden"
                                        } ${activeTab === "assigned" &&
                                            user.roles[0] === "Sales Representative"
                                            ? "!w-[272px]"
                                            : ""
                                        }`}
                                >
                                    {(hasUpdatePermission && activeTab === "pending" && !list_loading) && (<>
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                                            <div class="flex flex-col sm:items-end">
                                                <Button
                                                    size="sm" className="w-full sm:w-auto bg-[#F4B320] border border-[#F4B320] hover:border-[#607d8b1a] text-[#1A1918] capitalize shadow-none hover:shadow-none"
                                                    onClick={() => handleApprove(appointment)}
                                                >
                                                    {t("approveButton")}
                                                </Button>

                                                <p className="mt-2 text-xs text-gray-500">
                                                    {formatDateTime(appointment.created_at)}
                                                </p>
                                            </div>

                                            <div>
                                                <Button
                                                    variant="outlined"
                                                    size="sm" className="w-full sm:w-auto capitalize border-red-500 text-red-600"
                                                    onClick={() => handleReject(appointment)}
                                                >
                                                    {t("rejectButton")}
                                                </Button>

                                                <p className="mt-2 text-xs text-gray-500">
                                                    {t("approvalPending")}
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                    )}
                                    {(hasUpdatePermission && activeTab === "upcoming" && !list_loading) && (<>
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                                            {!appointmentDateTime.isBefore(moment()) &&
                                                <div>
                                                    <Button
                                                        size="sm" className="w-full text-[#1A1918] border border-[#F4B320] hover:border-[#607d8b1a]  capitalize sm:w-auto bg-amber-500 shadow-none hover:shadow-none"
                                                        onClick={() => setOpenAssignModal({ appointment_id: appointment?.id, appointment_date: appointment?.appointment_date, appointment_time: appointment?.appointment_time })}
                                                    >
                                                        {t("assignButton")}
                                                    </Button>
                                                </div>
                                            }
                                            {appointmentDateTime.isBefore(moment()) &&
                                                <div>
                                                    <Button
                                                        variant="outlined"
                                                        size="sm" className="w-full sm:w-auto border-green-500 capitalize text-green-600"
                                                        onClick={() => {
                                                            if ((appointment?.reschedule_count || 0) >= 3) {
                                                                Toaster.error(`${t("maximumRescheduleCountReached")}`);
                                                                return;
                                                            }
                                                            if (rescheduleAppointmentId === appointment.id) {
                                                                setRescheduleAppointmentId(null);
                                                            } else {
                                                                setRescheduleAppointmentId(appointment.id);
                                                                setRescheduleDate(appointment.appointment_date || todayDateString);
                                                                setRescheduleTime(appointment.appointment_time || "");
                                                                setRescheduleReason(""); // clear on open
                                                            }
                                                        }}
                                                    >
                                                        {t("rescheduleButton")}
                                                    </Button>

                                                    <p className="mt-2 text-xs text-gray-500">
                                                        {t("upToThreeTimes")}
                                                    </p>

                                                </div>
                                            }

                                            <div>
                                                <Button
                                                    variant="outlined"
                                                    size="sm" className="w-full sm:w-auto capitalize border border-[#736C64] text-[#736C64]"
                                                    onClick={() => handleCancel(appointment)}
                                                >
                                                    {ct("cancel")}
                                                </Button>
                                            </div>
                                        </div>
                                    </>)}
                                    {(hasUpdatePermission && activeTab === "assigned" && !list_loading && user.roles[0] === 'Sales Representative') && (<>
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-full">
                                            <div>
                                                <Popover
                                                    isOpen={isPopoverOpen.includes(index)}
                                                    positions={['bottom', 'left']}
                                                    padding={4}
                                                    content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-40 flex flex-col z-50 border border-gray-100 py-1 break-words">
                                                        <button
                                                            className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                            onClick={() => setOpenAssignTeamModal(appointment)}
                                                        >
                                                            {t("assignTeamButton")}
                                                        </button>
                                                    </div>}>
                                                    <IconButton variant="text" color="blue-gray" onClick={() => openActionPopover(index)}>
                                                        <EllipsisVerticalIcon className="h-6 w-6" />
                                                    </IconButton>
                                                </Popover>
                                            </div>
                                            <div>
                                                <Button
                                                    size="sm" className="w-full sm:w-auto bg-amber-500 shadow-none hover:shadow-none"
                                                    onClick={() => { handleConfirmAppointment(appointment?.id) }}
                                                >
                                                    {t("confirmAppointmentButton")}
                                                </Button>

                                                <p className="mt-2 text-xs text-gray-500">
                                                    {formatDateTime(appointment.created_at)}
                                                </p>
                                            </div>
                                            <div>
                                                <Button
                                                    variant="outlined"
                                                    size="sm" className="w-full sm:w-auto border-green-500 text-green-600"
                                                    onClick={() => handleCancel(appointment)}
                                                >
                                                    {ct("cancel")}
                                                </Button>
                                            </div>

                                        </div>
                                    </>)}
                                    {(hasUpdatePermission && activeTab === "confirmed" && !list_loading) && (<>
                                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                                            <div>
                                                <Button
                                                    size="sm" className="w-full text-[#1A1918] border border-[#F4B320] hover:border-[#607d8b1a] capitalize sm:w-auto bg-amber-500 shadow-none hover:shadow-none"
                                                    onClick={() => handleCompleteAppointment(appointment?.id)}
                                                >
                                                    {t("completedButton")}
                                                </Button>
                                            </div>

                                            {/* Relative Anchor Element for Popover Positioning */}
                                            <div className="relative">
                                                <Button
                                                    variant="outlined"
                                                    size="sm" className="w-full sm:w-auto border-green-500 capitalize text-green-600"
                                                    onClick={() => {
                                                        if ((appointment?.reschedule_count || 0) >= 3) {
                                                            Toaster.error(`${t("maximumRescheduleCountReached")}`);
                                                            return;
                                                        }
                                                        if (rescheduleAppointmentId === appointment.id) {
                                                            setRescheduleAppointmentId(null);
                                                        } else {
                                                            setRescheduleAppointmentId(appointment.id);
                                                            setRescheduleDate(appointment.appointment_date || todayDateString);
                                                            setRescheduleTime(appointment.appointment_time || "");
                                                            setRescheduleReason(""); // clear on open
                                                        }
                                                    }}
                                                >
                                                    {t("rescheduleButton")}
                                                </Button>

                                                <p className="mt-2 text-xs text-gray-500">
                                                    {t("upToThreeTimes")}
                                                </p>

                                                {/* Float Popover Form Layer UI */}
                                            </div>

                                        </div>
                                    </>)}
                                </div>
                            </div>
                        </Card>
                    );
                })}
                {
                    list_loading && (
                        <div className="flex justify-center py-8">
                            <div role="status">
                                <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                </svg>
                            </div>
                        </div>
                    )
                }
                {
                    !list_loading &&
                    appointments?.length === 0 && (
                        <div className="py-12 text-center text-gray-500">
                            {t("noAppointmentsFound")}
                        </div>
                    )
                }
            </div>

            <AssignAppointmentModal
                open={openAssignModal}
                onClose={() => setOpenAssignModal(false)}
                appointment={openAssignModal}
                onSubmited={() => {
                    setOpenAssignModal(false);
                    fetchAppointments();
                }}
            />
            {rescheduleAppointmentId && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                    <div className="relative w-full max-w-xl rounded-xl bg-white shadow-2xl">

                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <h2 className="text-lg font-semibold">
                                {t("rescheduleAppointmentTitle")}
                            </h2>

                            <button
                                onClick={() => setRescheduleAppointmentId(null)}
                                className="text-2xl leading-none text-gray-500 hover:text-black"
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        <form
                            onSubmit={(e) =>
                                handleRescheduleSubmit(
                                    e,
                                    rescheduleAppointmentId
                                )
                            }
                            className="space-y-5 p-6"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {t("newDateLabel")}
                                    </label>

                                    <input
                                        type="date"
                                        min={todayDateString}
                                        // value={rescheduleDate}
                                        onChange={(e) =>
                                            setRescheduleDate(e.target.value)
                                        }
                                        required
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {t("newTimeSlotLabel")}
                                    </label>

                                    <select
                                        value={rescheduleTime}
                                        onChange={(e) => setRescheduleTime(e.target.value)}
                                        required
                                        disabled={loading || !rescheduleDate}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-gray-100"
                                    >
                                        <option value="">
                                            {loading ? `${t("loadingSlots")}` : `${t("selectTimeSlot")}`}
                                        </option>

                                        {!loading && availableSlots?.length === 0 && (
                                            <option value="" disabled>{t("noActiveSlotsFound")}</option>
                                        )}

                                        {availableSlots?.map((slot) => {
                                            let isPast = false;

                                            // Check if slot has already passed for today's date
                                            if (rescheduleDate === todayDateString) {
                                                const now = new Date();
                                                const currentHours = now.getHours();
                                                const currentMinutes = now.getMinutes();

                                                const match = slot.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);

                                                if (match) {
                                                    let [_, hoursStr, minutesStr, modifier] = match;
                                                    let hours = parseInt(hoursStr, 10);
                                                    const minutes = parseInt(minutesStr, 10);

                                                    if (modifier) {
                                                        if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
                                                        if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
                                                    }

                                                    if (hours < currentHours) {
                                                        isPast = true;
                                                    } else if (hours === currentHours && minutes <= currentMinutes) {
                                                        isPast = true;
                                                    }
                                                }
                                            }

                                            const isSlotDisabled = !slot.is_available || isPast;

                                            return (
                                                <option
                                                    key={slot.time}
                                                    value={slot.time}
                                                    disabled={isSlotDisabled}
                                                >
                                                    {slot.time}
                                                    {isPast ? ` (${t("passedSlot")})` : !slot.is_available ? ` (${t("alreadyBooked")})` : ""}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="text-sm font-medium">
                                        {t("reasonForRescheduleLabel")}
                                    </label>

                                    <span
                                        className={`text-xs ${rescheduleReason.trim().length < 10 ||
                                            rescheduleReason.trim().length > 500
                                            ? "text-red-500"
                                            : "text-gray-500"
                                            }`}
                                    >
                                        {rescheduleReason.trim().length}/500
                                    </span>
                                </div>

                                <textarea
                                    rows={6}
                                    value={rescheduleReason}
                                    onChange={(e) =>
                                        setRescheduleReason(e.target.value)
                                    }
                                    placeholder={t("enterReasonPlaceholder")}
                                    className="w-full rounded-md border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2">

                                <Button
                                    variant="outlined"
                                    color="gray"
                                    onClick={() =>
                                        setRescheduleAppointmentId(null)
                                    }
                                >
                                    {ct("cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    className="bg-green-600 shadow-none hover:shadow-none"
                                    disabled={
                                        rescheduleReason.trim().length < 10 ||
                                        rescheduleReason.trim().length > 500
                                    }
                                >
                                    {ct("save")}
                                </Button>

                            </div>
                        </form>
                    </div>
                </div>
            )}
            <AssignTeamModal
                open={openAssignTeamModal}
                onClose={() => setOpenAssignTeamModal(false)}
                appointment={openAssignTeamModal}
                onSubmited={() => {
                    setOpenAssignTeamModal(false);
                    fetchAppointments();
                }}
            />
            <CustomerNotificationLogModal
                open={isViewLog}
                onClose={() => setIsViewLog(false)}
                appointment={isViewLog}
            />
        </div>
    );
}
